import React, { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import {
  format, parse, startOfWeek, getDay,
  addWeeks, addDays, parseISO, startOfDay, endOfDay, isSameDay,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { fetchPartners, fetchCourierAvailability } from "api";
import { PageHeader, Spinner, Select } from "components/common";
import type { DayOfWeek } from "types";
import { pacificDateTime } from "utils/scheduleTime";

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { "en-US": enUS },
});

const COURIER_COLORS = [
  "#6366f1", // indigo
  "#0891b2", // cyan
  "#059669", // emerald
  "#d97706", // amber
  "#dc2626", // red
  "#7c3aed", // violet
  "#2563eb", // blue
  "#c026d3", // fuchsia
  "#ea580c", // orange
  "#0d9488", // teal
];

const DAY_OFF_STATUS_COLORS = [
  "#fcba03", // yellow - requested
  "#47c94b", // green - approved
  "#c94747", // red - rejected
];

const CALENDAR_TEXT_COLOR = "#262626";

const DAY_OFFSETS: Record<DayOfWeek, number> = {
  monday:    0,
  tuesday:   1,
  wednesday: 2,
  thursday:  3,
  friday:    4,
  saturday:  5,
  sunday:    6,
};

interface CalEvent {
  title:      string;
  start:      Date;
  end:        Date;
  color:      string;
  courierId?: number;
  allDay?:    boolean;
  isDayOff?:  boolean;
  status?:    string;
}

// Returns the segments of [availStart, availEnd] that remain after cutting out
// every overlapping day-off range. Handles complete removal, trimming, and
// splitting (when a day-off sits fully inside an availability block).
function applyDayOffOverlaps(
  availStart: Date,
  availEnd:   Date,
  offRanges:  Array<{ start: Date; end: Date, status: string }>,
): Array<{ start: Date; end: Date }> {
  let segments: Array<{ start: Date; end: Date }> = [{ start: availStart, end: availEnd }];

  for (const off of offRanges) {
    if (off.status !== 'approved') {
      continue;
    }
    const next: Array<{ start: Date; end: Date }> = [];
    for (const seg of segments) {
      // No overlap — keep as-is
      if (off.end <= seg.start || off.start >= seg.end) {
        next.push(seg);
        continue;
      }
      // Day-off fully contains the segment → drop it
      if (off.start <= seg.start && off.end >= seg.end) continue;
      // Day-off overlaps only the start → trim start forward
      if (off.start <= seg.start) { next.push({ start: off.end, end: seg.end }); continue; }
      // Day-off overlaps only the end → trim end backward
      if (off.end >= seg.end)     { next.push({ start: seg.start, end: off.start }); continue; }
      // Day-off sits entirely inside → split into two segments
      next.push({ start: seg.start, end: off.start });
      next.push({ start: off.end,   end: seg.end   });
    }
    segments = next;
  }

  return segments;
}

// Availability slots are Pacific wall-clock "HH:MM:SS" on a Pacific day key.
// Returns the slot's start/end instants; an end at or before the start
// (e.g. 20:00–00:00) runs through midnight, matching the backend scheduler.
function slotInterval(date: Date, slot: { start: string; end: string }): { start: Date; end: Date } {
  const dateStr = format(date, "yyyy-MM-dd");
  const start   = pacificDateTime(dateStr, slot.start);
  const end     = slot.end > slot.start
    ? pacificDateTime(dateStr, slot.end)
    : pacificDateTime(format(addDays(date, 1), "yyyy-MM-dd"), "00:00:00");
  return { start, end };
}

export default function CourierCalendarPage() {
  const [weekOffset, setWeekOffset]             = useState(0);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [hiddenCourierIds, setHiddenCourierIds]   = useState<Set<number>>(new Set());

  const weekStart = addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset);

  // ── Partners ────────────────────────────────────────────────────────────────
  const { data: partners = [], isLoading: loadingPartners } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  // Default to first partner once loaded
  const effectivePartnerId = selectedPartnerId ?? partners[0]?.id ?? null;

  // Reset hidden couriers whenever the partner changes
  useEffect(() => { setHiddenCourierIds(new Set()); }, [effectivePartnerId]);

  // ── Courier availability for selected partner ────────────────────────────
  const { data: couriers = [], isLoading: loadingCouriers } = useQuery({
    queryKey: ["courierAvailability", effectivePartnerId],
    queryFn:  () => fetchCourierAvailability(effectivePartnerId!),
    enabled:  effectivePartnerId !== null,
  });

  // ── Assign colors per courier ────────────────────────────────────────────
  const courierColors = useMemo(() => {
    const map: Record<number, string> = {};
    couriers.forEach((c, i) => { map[c.id] = COURIER_COLORS[i % COURIER_COLORS.length]; });
    return map;
  }, [couriers]);

  const courierNames = useMemo(() => {
    const map: Record<number, string> = {};
    couriers.forEach((c) => { map[c.id] = `${c.name_1}${c.name_2 ? ` ${c.name_2}` : ""}`; });
    return map;
  }, [couriers]);

  // ── Build calendar events ────────────────────────────────────────────────
  const events = useMemo<CalEvent[]>(() => {
    const result: CalEvent[] = [];

    couriers.forEach((courier) => {
      const color = courierColors[courier.id];
      const name  = courierNames[courier.id];

      // ── Pass 1: Days off → collect timed ranges + push events ─────────────
      const dayOffRanges: Array<{ start: Date; end: Date; status: string }> = [];

      courier.days_off.forEach((d) => {
        const dStart = parseISO(d.start_at);
        const dEnd   = parseISO(d.end_at);
        const status = d.status;
        const title = `${name} – ${status === 'approved' ? 'Off' : status}`;

        // All-day banner in the header row
        result.push({
          title:     title,
          start:     startOfDay(dStart),
          end:       endOfDay(dEnd),
          color,
          courierId: courier.id,
          allDay:    true,
          isDayOff:  true,
          status:    d.status,
        });

        // Per-day timed blocks
        let current = startOfDay(dStart);
        const lastDay = startOfDay(dEnd);
        while (current <= lastDay) {
          const slotStart = isSameDay(current, dStart)
            ? dStart
            : new Date(new Date(current).setHours(0, 0, 0, 0));
          const slotEnd = isSameDay(current, dEnd)
            ? dEnd
            : new Date(new Date(current).setHours(23, 59, 0, 0));

          dayOffRanges.push({ start: slotStart, end: slotEnd, status: status });
          result.push({
            title:     title,
            start:     slotStart,
            end:       slotEnd,
            color,
            courierId: courier.id,
            isDayOff:  true,
            status:    status,
          });
          current = addDays(current, 1);
        }
      });

      // ── Pass 2: Availability slots (ad-hoc overrides recurring for this week)
      const weekKey = format(weekStart, "yyyy-MM-dd");
      const adHoc = (courier.ad_hoc_schedules ?? []).find((s) => s.week_start_date === weekKey);
      const activeAvailability = adHoc ? adHoc.availability : courier.availability;

      (Object.entries(activeAvailability) as [DayOfWeek, Array<{ start: string; end: string }>][])
        .forEach(([day, slots]) => {
          const offset = DAY_OFFSETS[day];
          if (offset === undefined) return;
          const dayDate = addDays(weekStart, offset);

          slots.forEach((slot) => {
            const { start: slotStart, end: slotEnd } = slotInterval(dayDate, slot);

            const segments = applyDayOffOverlaps(slotStart, slotEnd, dayOffRanges);
            segments.forEach((seg) => {
              result.push({ title: name, start: seg.start, end: seg.end, color, courierId: courier.id });
            });
          });
        });
    });

    return result;
  }, [couriers, courierColors, courierNames, weekStart]);

  const visibleEvents = useMemo(
    () => events.filter((e) => !e.courierId || !hiddenCourierIds.has(e.courierId)),
    [events, hiddenCourierIds],
  );

  const toggleCourier = (id: number) =>
    setHiddenCourierIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const eventPropGetter = (event: CalEvent) => {
    if (event.isDayOff) {
      let color = DAY_OFF_STATUS_COLORS[0]; // default to requested
      let borderStyle = 'dashed';
      switch (event.status) {
        case 'approved':
          color = DAY_OFF_STATUS_COLORS[1];
          borderStyle = 'solid';
          break;
        case 'rejected':
          color = DAY_OFF_STATUS_COLORS[2];
          borderStyle = 'solid';
          break;
      }
      return {
        style: {
          backgroundColor: `${color}30`,
          border:           `2px ${borderStyle} ${color}`,
          borderRadius:     "4px",
          color:            CALENDAR_TEXT_COLOR,
          fontStyle:        "italic",
          padding:          "1px 4px",
        },
      };
    }
    return {
      style: {
        backgroundColor: `${event.color}20`,
        borderLeft:      `3px solid ${event.color}`,
        borderTop:       "none",
        borderRight:     "none",
        borderBottom:    "none",
        borderRadius:    "4px",
        color:           "#1f2937",
        padding:         "2px 4px",
      },
    };
  };

  const isLoading = loadingPartners || (effectivePartnerId !== null && loadingCouriers);

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader
        title="Courier availability calendar"
        subtitle="Weekly availability and time away for couriers by partner"
      />

      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        {/* Partner selector */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600" htmlFor="partner-select">
            Partner
          </label>
          <Select
            id="partner-select"
            size="sm"
            className="w-56"
            value={String(effectivePartnerId ?? "")}
            onChange={(e) => setSelectedPartnerId(Number(e.target.value))}
            disabled={loadingPartners || partners.length === 0}
          >
            {partners.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </div>

        {/* Week navigation */}
        <div className="flex items-center gap-2 ml-auto">
            <button
              className="btn-accent px-3 py-1.5 text-xs"
              disabled={weekOffset == 0}
              onClick={() => setWeekOffset(0)}
            >
              Today
            </button>
          <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setWeekOffset((w) => w - 1)}>
            ← Prev
          </button>
          <span className="text-sm font-medium text-gray-700 min-w-[160px] text-center">
            {format(weekStart, "MMM d")} – {format(addDays(weekStart, 6), "MMM d, yyyy")}
          </span>
          <button className="btn-secondary px-3 py-1.5 text-xs" onClick={() => setWeekOffset((w) => w + 1)}>
            Next →
          </button>
        </div>
      </div>

      {isLoading ? (
        <Spinner className="mt-12" />
      ) : (
        <>
          {/* Courier legend — click to toggle visibility */}
          {couriers.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {couriers.map((courier) => {
                const hidden = hiddenCourierIds.has(courier.id);
                const color  = courierColors[courier.id];
                return (
                  <button
                    key={courier.id}
                    onClick={() => toggleCourier(courier.id)}
                    className="flex items-center gap-1.5 text-xs rounded-md px-2 py-1 border transition-colors select-none"
                    style={{
                      borderColor:     hidden ? "#e5e7eb" : color,
                      backgroundColor: hidden ? "transparent" : `${color}15`,
                      color:           hidden ? "#9ca3af" : "#374151",
                    }}
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-sm flex-shrink-0 transition-colors"
                      style={{ backgroundColor: hidden ? "#d1d5db" : color }}
                    />
                    <span style={{ textDecoration: hidden ? "line-through" : "none" }}>
                      {courierNames[courier.id]}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {couriers.length === 0 && effectivePartnerId !== null && (
            <p className="text-sm text-gray-500 mt-4">No active couriers found for this partner.</p>
          )}

          <div style={{ height: "calc(100vh - 320px)", minHeight: 500 }} className="courier-calendar">
            <Calendar<CalEvent>
              localizer={localizer}
              events={visibleEvents}
              view={Views.WEEK}
              date={weekStart}
              onNavigate={() => {}}
              onView={() => {}}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              components={{ toolbar: () => null }}
              eventPropGetter={eventPropGetter}
              step={30}
              timeslots={2}
            />
          </div>
        </>
      )}
    </div>
  );
}
